import React, { useState, useEffect } from "react";
import {
  FaUsers,
  FaUserCheck,
  FaUserAlt,
  FaUserPlus,
  FaUserSlash,
} from "react-icons/fa";
import { GiLovers } from "react-icons/gi";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import ReportSection from "../../../Components/ReportSection/ReportSection";
import { authorizedGet } from "../../../Config/networkWithToken";
import configuration from "../../../Config/configuration";
import {
  config,
  getValueFromLocalStorage,
  localStorageKey,
} from "../../../utils/helper";
import Spinner from "../../../Components/Spinner/Spinner";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DashBoard = () => {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonthIndex = date.getMonth();
  const currentMonth = months[currentMonthIndex];
  const [loading, setLoading] = useState(false);
  const [filteredStats, setFilteredStats] = useState([]);
  const [totalUser, setTotalUser] = useState(false);
  const [sortBy, setSortBy] = useState("thisYear");
  const [startDate, setStartDate] = useState({ month: "Oct", year: 2020 });
  const [endDate, setEndDate] = useState({ month: currentMonth, year: currentYear });
  const [fullReportData, setFullReportData] = useState(null);
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [sortBy, startDate, endDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${configuration.apis.getReport}?adminId=${adminId}`;
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth();

      const formatDate = (date) => {
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      };

      if (sortBy === "thisYear") {
        url += `&date=this_year`;
      } else if (sortBy === "thisMonth") {
        url += `&date=this_month`;
      } else if (sortBy === "previousYear") {
        url += `&date=previous_year`;
      } else if (sortBy === "custom") {
        const getMonthIndex = (monthName) => months.indexOf(monthName);
        const start = new Date(startDate.year, getMonthIndex(startDate.month), 1);
        const isCurrentMonth =
          endDate.year === currentYear && endDate.month === currentMonth;
        const end = isCurrentMonth
          ? new Date()
          : new Date(endDate.year, getMonthIndex(endDate.month) + 1, 0);
        url += `&dateFrom=${formatDate(start)}&dateTo=${formatDate(end)}`;
      } else if (["q1", "q2", "q3", "q4", "h1", "h2"].includes(sortBy)) {
        let start, end;
        switch (sortBy) {
          case "q1":
            start = new Date(currentYear, 0, 1);
            end = new Date(currentYear, 2, 31);
            break;
          case "q2":
            start = new Date(currentYear, 3, 1);
            end = new Date(currentYear, 5, 30);
            break;
          case "q3":
            start = new Date(currentYear, 6, 1);
            end = new Date(currentYear, 8, 30);
            break;
          case "q4":
            start = new Date(currentYear, 9, 1);
            end = new Date(currentYear, 11, 31);
            break;
          case "h1":
            start = new Date(currentYear, 0, 1);
            end = new Date(currentYear, 5, 30);
            break;
          case "h2":
            start = new Date(currentYear, 6, 1);
            end = new Date(currentYear, 11, 31);
            break;
        }
        url += `&dateFrom=${formatDate(start)}&dateTo=${formatDate(end)}`;
      }

      const res = await authorizedGet(url, config);
      const responseData = res?.data?.data?.UserRecord || [];

      setFullReportData(res?.data?.data);
      setTotalUser(res?.data?.data?.totalUsers);

      const monthOrder = [
        "jan", "feb", "mar", "apr", "may", "jun",
        "jul", "aug", "sep", "oct", "nov", "dec",
      ];

      const formatted = responseData
        .map((item) => ({
          month: item.month || item.week || item.year || "Unknown",
          Total: item.total || 0,
          Registered: item.registerUsers || 0,
          RegisterMale: item.registerMaleUsers || 0,
          RegisterFemale: item.registerFemaleUsers || 0,
          Active: item.active || 0,
          ActiveMale: item.activeMale || 0,
          ActiveFemale: item.activeFemale || 0,
          Signup: item.signupUsers || 0,
          SignupMale: item.signupMaleUsers || 0,
          SignupFemale: item.signupFemaleUsers || 0,
        }))
        .sort((a, b) => {
          const indexA = monthOrder.indexOf(a.month);
          const indexB = monthOrder.indexOf(b.month);
          return indexA - indexB;
        });

      setFilteredStats(formatted);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableMonths = (year) => {
    if (year === 2020) return months.slice(9);
    if (year === currentYear) return months.slice(0, currentMonthIndex + 1);
    return months;
  };

  const cards = [
    {
      icon: <FaUsers />,
      head: "Total Candidates",
      users: totalUser?.allUsers || 0,
      bgColor: "bg-gradient-to-r from-yellow-400 to-yellow-600",
    },
    {
      icon: <FaUserCheck />,
      head: "Active Users",
      users: totalUser?.activeUsers || 0,
      bgColor: "bg-gradient-to-r from-green-400 to-green-600",
    },
    {
      icon: <FaUserAlt />,
      head: "Registered Users",
      users: totalUser?.registerUsers || 0,
      bgColor: "bg-gradient-to-r from-cyan-500 to-cyan-700",
    },
    {
      icon: <FaUserPlus />,
      head: "Signup Users",
      users: totalUser?.signupUsers || 0,
      bgColor: "bg-gradient-to-r from-pink-400 to-pink-600",
    },
  ];

return (
  <div className="mx-auto w-[97%] pb-12">

    {/* 1. Cards */}
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((item, index) => (
        <div key={index} className={`flex flex-col items-center justify-center rounded-xl p-4 text-white ${item.bgColor}`}>
          <div className="mb-2 text-3xl">{item.icon}</div>
          <h3 className="text-lg font-semibold">{item.head}</h3>
          <p className="text-2xl font-bold">{item.users}</p>
        </div>
      ))}
    </div>

    {/* 2. Blocked & Married */}
    <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="flex items-center justify-between rounded-xl bg-red-100 p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-700">Blocked Users</h3>
            <p className="text-2xl font-bold text-red-600">{totalUser?.blockedusers}</p>
          </div>
        </div>
        <div className="rounded-full bg-red-200 p-3 text-red-600">
          <FaUserSlash className="text-2xl" />
        </div>
      </div>
      <div className="flex items-center justify-between rounded-xl bg-indigo-100 p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <div>
            <h3 className="text-base font-semibold text-gray-700">Married Users</h3>
            <p className="text-2xl font-bold text-indigo-600">{totalUser?.marriedUsers}</p>
          </div>
        </div>
        <div className="rounded-full bg-indigo-200 p-3 text-indigo-600">
          <GiLovers className="text-2xl" />
        </div>
      </div>
    </div>

    <ReportSection reportData={fullReportData} showOnly={["User Metrics"]} />

    {/* 4. Sort by filter */}
    <div className="mt-4 flex flex-col justify-end gap-2 sm:flex-row sm:items-center">
      sort by:
      <select
        value={sortBy}
        onChange={(e) => setSortBy(e.target.value)}
        className="rounded-lg border px-4 py-2 text-sm shadow-sm outline-none"
      >
        <option value="thisMonth">This Month ({currentMonth})</option>
        <option value="thisYear">This Year ({currentYear})</option>
        <option value="previousYear">Previous Year ({currentYear - 1})</option>
        <option value="custom">Custom Range</option>
        <option value="q1">Q1 (Jan - Mar)</option>
        <option value="q2">Q2 (Apr - Jun)</option>
        <option value="q3">Q3 (Jul - Sep)</option>
        <option value="q4">Q4 (Oct - Dec)</option>
        <option value="h1">H1 (Jan - Jun)</option>
        <option value="h2">H2 (Jul - Dec)</option>
      </select>
      {sortBy === "custom" && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1">
            <select value={startDate.month} onChange={(e) => setStartDate({ ...startDate, month: e.target.value })} className="rounded border px-2 py-1 text-sm">
              {getAvailableMonths(startDate.year).map((m) => (<option key={m}>{m}</option>))}
            </select>
            <select value={startDate.year} onChange={(e) => setStartDate({ ...startDate, year: parseInt(e.target.value), month: getAvailableMonths(parseInt(e.target.value))[0] })} className="rounded border px-2 py-1 text-sm">
              {Array.from({ length: 6 }).map((_, idx) => { const y = currentYear - 5 + idx; return <option key={y}>{y}</option>; })}
            </select>
          </div>
          <span className="px-1 text-sm text-gray-600">to</span>
          <div className="flex gap-1">
            <select value={endDate.month} onChange={(e) => setEndDate({ ...endDate, month: e.target.value })} className="rounded border px-2 py-1 text-sm">
              {getAvailableMonths(endDate.year).map((m) => (<option key={m}>{m}</option>))}
            </select>
            <select value={endDate.year} onChange={(e) => setEndDate({ ...endDate, year: parseInt(e.target.value), month: getAvailableMonths(parseInt(e.target.value))[0] })} className="rounded border px-2 py-1 text-sm">
              {Array.from({ length: 6 }).map((_, idx) => { const y = currentYear - 5 + idx; return <option key={y}>{y}</option>; })}
            </select>
          </div>
        </div>
      )}
    </div>

    {/* 5. Chart */}
    <div className="mt-12 rounded-2xl bg-white p-6">
<div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
  <div>
    <h2 className="text-2xl font-bold text-gray-800">User Growth & Engagement</h2>
    <p className="text-sm text-gray-500">Overview of user activity</p>
  </div>
  <button
    onClick={() => setShowTable(!showTable)}
    className={`rounded px-4 py-2 text-sm text-white ${showTable ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"}`}
  >
    {showTable ? "Close Table" : "Show Table"}
  </button>
</div>
      <div className="h-96 w-full">
        {loading ? (
          <div className="flex h-full items-center justify-center"><Spinner /></div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={filteredStats} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorRegistered" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorActiveMale" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorActiveFemale" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ec4899" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorSignupUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#33f97fff" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#33f97fff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" />
              <YAxis />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip />
              <Legend />
              <Area type="monotone" dataKey="Total" stroke="#f59e0b" fillOpacity={1} fill="url(#colorRegistered)" dot={{ r: 4, stroke: "#f59e0b", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="Registered" stroke="#8b5cf6" fillOpacity={0.1} dot={{ r: 4, stroke: "#8b5cf6", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="RegisterMale" stroke="#06b6d4" fillOpacity={1} fill="url(#colorActiveMale)" dot={{ r: 4, stroke: "#06b6d4", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="RegisterFemale" stroke="#ec4899" fillOpacity={1} fill="url(#colorActiveFemale)" dot={{ r: 4, stroke: "#ec4899", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="Active" stroke="#22c55e" fillOpacity={0.1} dot={{ r: 4, stroke: "#22c55e", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="ActiveMale" stroke="#10b981" fillOpacity={0.1} dot={{ r: 4, stroke: "#10b981", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="ActiveFemale" stroke="#f43f5e" fillOpacity={0.1} dot={{ r: 4, stroke: "#f43f5e", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="Signup" stroke="#f426c7ff" fillOpacity={1} fill="url(#colorSignupUsers)" dot={{ r: 4, stroke: "#f426c7ff", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="SignupMale" stroke="#818cf8" fillOpacity={0.1} dot={{ r: 4, stroke: "#818cf8", strokeWidth: 2, fill: "white" }} />
              <Area type="monotone" dataKey="SignupFemale" stroke="#fb7185" fillOpacity={0.1} dot={{ r: 4, stroke: "#fb7185", strokeWidth: 2, fill: "white" }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
    {/* User Growth Table */}
    {showTable && (
<div className="mt-6 overflow-x-auto rounded-2xl bg-white p-6">
  <h2 className="mb-4 text-2xl font-bold text-gray-800">User Growth Table</h2>
  <table className="w-full table-auto border border-gray-300 text-sm">
    <thead className="bg-gray-100">
      <tr>
        <th className="border px-3 py-2 text-left">Month</th>
        <th className="border px-3 py-2 text-center">Total</th>
        <th className="border px-3 py-2 text-center">Registration</th>
        <th className="border px-3 py-2 text-center">Register Male</th>
        <th className="border px-3 py-2 text-center">Register Female</th>
        <th className="border px-3 py-2 text-center">Active</th>
        <th className="border px-3 py-2 text-center">Active Male</th>
        <th className="border px-3 py-2 text-center">Active Female</th>
        <th className="border px-3 py-2 text-center">Signup</th>
        <th className="border px-3 py-2 text-center">Signup Male</th>
        <th className="border px-3 py-2 text-center">Signup Female</th>
      </tr>
    </thead>
    <tbody>
      {filteredStats.map((item, index) => (
        <tr key={index} className="hover:bg-gray-50">
          <td className="border px-3 py-2 font-medium capitalize">{item.month}</td>
          <td className="border px-3 py-2 text-center">{item.Total}</td>
          <td className="border px-3 py-2 text-center">{item.Registered}</td>
          <td className="border px-3 py-2 text-center">{item.RegisterMale}</td>
          <td className="border px-3 py-2 text-center">{item.RegisterFemale}</td>
          <td className="border px-3 py-2 text-center">{item.Active}</td>
          <td className="border px-3 py-2 text-center">{item.ActiveMale}</td>
          <td className="border px-3 py-2 text-center">{item.ActiveFemale}</td>
          <td className="border px-3 py-2 text-center">{item.Signup}</td>
          <td className="border px-3 py-2 text-center">{item.SignupMale}</td>
          <td className="border px-3 py-2 text-center">{item.SignupFemale}</td>
        </tr>
      ))}
      {filteredStats.length === 0 && (
        <tr>
          <td colSpan={10} className="border px-3 py-6 text-center text-gray-400">
            No data available
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>
    )}

    <ReportSection
      reportData={fullReportData}
      showOnly={[
        "Mentor Verification",
        "Marital Status Breakdown",
        "Age-wise Breakdown",
        "Age-wise Register and Signup and Active",
        "Users Requests Summary",
        "Admin Suggestions",
        "Questionnaire Requests Summary",
        "Declined Reasons Summary",
        "Admin Notes Made",
      ]}
    />

  </div>
);
};

export default DashBoard;